import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * 解析 LRC 格式文字，返回 [{ time, text }] 陣列
 */
function parseLRC(lrcText) {
  const lines = lrcText.split(/\r?\n/);
  const lyrics = [];
  const timeRegex = /\[(\d{2}):(\d{2}(?:\.\d{2})?)\]/;
  lines.forEach(line => {
    const match = line.match(timeRegex);
    if (match) {
      const minutes = parseInt(match[1], 10);
      const seconds = parseFloat(match[2]);
      const time = minutes * 60 + seconds;
      const text = line.replace(timeRegex, '').trim();
      lyrics.push({ time, text });
    }
  });
  return lyrics.sort((a, b) => a.time - b.time);
}

/* 自訂 Hook：根據螢幕寬高比來判斷是否為直屏（Portrait） */
function useOrientation() {
  const [isPortrait, setIsPortrait] = useState(window.innerWidth / window.innerHeight < 1);
  useEffect(() => {
    const handleResize = () => {
      const ratio = window.innerWidth / window.innerHeight;
      setIsPortrait(ratio < 1);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isPortrait;
}

export default function MusicWithLyrics() {
  const isPortrait = useOrientation();
  const [lyrics, setLyrics] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // 讀取並解析 LRC 檔案
  useEffect(() => {
    fetch('/music/background.lrc')
      .then(response => response.text())
      .then(text => {
        const parsed = parseLRC(text);
        setLyrics(parsed);
      })
      .catch(err => console.error('讀取 LRC 檔案錯誤:', err));
  }, []);

  // 根據音樂播放進度更新 currentIndex
  useEffect(() => {
    const audio = document.getElementById('bg-music-with-lyrics');
    if (!audio) return;
    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      let i = lyrics.findIndex(item => item.time > currentTime);
      if (i === -1) {
        setCurrentIndex(lyrics.length - 1);
      } else if (i > 0) {
        setCurrentIndex(i - 1);
      } else {
        setCurrentIndex(0);
      }
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [lyrics]);

  // 同步系統播放狀態
  useEffect(() => {
    const audio = document.getElementById('bg-music-with-lyrics');
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  // 控制音樂播放／靜音
  useEffect(() => {
    const audio = document.getElementById('bg-music-with-lyrics');
    if (!audio) return;
    if (isPlaying) {
      audio.play().catch(err => console.error(err));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  // ── 橫屏格式 ──
  // 使用四行歌詞，行高 24px，總高度 96px
  const lineHeight = 24; // px
  const landscapeContainerHeight = 96; // px
  const getLine = (index) => lyrics[index]?.text ?? "";
  const line0 = getLine(currentIndex - 1);
  const line1 = getLine(currentIndex);
  const line2 = getLine(currentIndex + 1);
  const line3 = getLine(currentIndex + 2);

  // ── 直屏格式 ──
  // 僅顯示兩行歌詞，容器高度 64px
  const portraitContainerHeight = 64; // px
  const currentLine = getLine(currentIndex);
  const nextLine = getLine(currentIndex + 1);

  // Logo 樣式保持原設計（內部含紅線效果）
  const logoStyle = {
    width: 50,
    height: 50,
    backgroundColor: "rgba(255,255,255,0.8)",
    border: "1px solid black",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    fontWeight: "bold",
    color: isPlaying ? "black" : "red",
    cursor: "pointer",
    zIndex: 10,
    position: "relative",
  };

  // ── 橫屏容器 ──
  // 固定在右上角，總尺寸 300×100px，分為左右兩欄：左側 210px（歌詞）、右側 50px（logo）
  const landscapeContainerStyle = {
    width: "300px",
    height: "100px",
    position: "fixed",
    top: "2rem",
    right: "1rem",
    zIndex: 50,
    display: "flex",
    flexDirection: "row",
  };

  // 歌詞區塊（橫屏）：寬度 250px，高度 100%，利用 flex 置中
  const landscapeLyricsStyle = {
    width: "250px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  };

  // logo 區塊（橫屏）：寬度固定 50px，高度 100%，內容置中
  const landscapeLogoContainerStyle = {
    width: "50px",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "felx-start",
  };

  // 渲染橫屏的歌詞，利用 flex 置中
  const renderLyricsLandscape = () => (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0.5 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ width: "100%", height: "100%" }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ height: lineHeight }} className="text-sm text-gray-300">{line0}</div>
          <div style={{ height: lineHeight }} className="text-base font-bold">{line1}</div>
          <div style={{ height: lineHeight }} className="text-sm text-gray-300">{line2}</div>
          <div style={{ height: lineHeight }} className="text-sm text-gray-300">{line3}</div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  // ── 直屏容器 ──
  // 固定在螢幕下方，貼齊螢幕（left/right/bottom: 0），高度 64px，背景半透明，內容置中
  const portraitContainerStyle = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: `${portraitContainerHeight}px`,
    zIndex: 50,
    backgroundColor: "rgba(204,204,204,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  // 渲染直屏的歌詞（兩行），字型較大，置中顯示
  const renderLyricsPortrait = () => (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0.5 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ width: "100%", height: "100%" }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="text-xl font-bold">{currentLine}</div>
          <div className="text-lg text-gray-300">{nextLine}</div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  const toggleMusic = () => setIsPlaying(!isPlaying);

  if (isPortrait) {
    // 直屏格式：下方橫幅固定貼齊螢幕底部，內容置中，logo 置右
    return (
      <>
        <div style={portraitContainerStyle}>
          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            {renderLyricsPortrait()}
            <div
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <div style={logoStyle} onClick={toggleMusic}>
                {"♪"}
                {!isPlaying && (
                  <div
                    style={{
                      position: "absolute",
                      width: "120%",
                      height: 2,
                      backgroundColor: "red",
                      transform: "rotate(45deg)",
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
        <audio id="bg-music-with-lyrics" src="/music/background.mp3" loop />
      </>
    );
  } else {
    // 橫屏格式：右上固定容器，分左右兩欄，左側歌詞置中，右側 logo
    return (
      <>
        <div style={landscapeContainerStyle}>
          <div style={landscapeLyricsStyle}>
            {renderLyricsLandscape()}
          </div>
          <div style={landscapeLogoContainerStyle}>
            <div style={logoStyle} onClick={toggleMusic}>
              {"♪"}
              {!isPlaying && (
                <div
                  style={{
                    position: "absolute",
                    width: "120%",
                    height: 2,
                    backgroundColor: "red",
                    transform: "rotate(45deg)",
                  }}
                ></div>
              )}
            </div>
          </div>
        </div>
        <audio id="bg-music-with-lyrics" src="/music/background.mp3" loop />
      </>
    );
  }
}
